import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Globe, MousePointer } from 'lucide-react';

const INTL_IMG = 'https://universitem.hacettepe.edu.tr/wp-content/uploads/2021/04/hakkinda08.jpg';

const InternationalBanner = ({ isEn, prefix }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
    className="relative overflow-hidden rounded-3xl mb-14 min-h-[340px] md:min-h-[420px] flex items-end"
  >
    {/* Fotoğraf — object-position top ile üst kısmı görünür */}
    <img
      src={INTL_IMG}
      alt="International Students"
      className="absolute inset-0 w-full h-full object-cover object-top"
    />

    {/* Koyu gradient overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

    {/* Dekoratif daireler sağda */}
    <div className="absolute top-6 right-10 w-40 h-40 rounded-full border border-white/10 pointer-events-none" />
    <div className="absolute top-12 right-16 w-24 h-24 rounded-full border border-white/8 pointer-events-none" />
    <div className="absolute -top-4 right-4 w-56 h-56 rounded-full border border-white/5 pointer-events-none" />

    {/* İçerik */}
    <div className="relative z-10 w-full flex flex-col md:flex-row md:items-end justify-between gap-6 p-7 md:p-10">
      <div className="max-w-xl">
        {/* Üst etiket */}
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
          <Globe className="w-3 h-3 text-blue-300" />
          <span className="text-white/75 text-[10px] font-semibold uppercase tracking-[0.2em]">
            {isEn ? 'International Students · 2026–2027' : 'Uluslararası Öğrenciler · 2026–2027'}
          </span>
        </div>

        <h2 className="text-2xl md:text-3xl lg:text-[2.6rem] font-black text-white leading-tight mb-4">
          {isEn
            ? <>Welcome to<br /><span className="text-blue-300">Hacettepe University</span><br />Faculty of Science</>
            : <>Hacettepe Üniversitesi<br /><span className="text-blue-300">Fen Fakültesi</span>'ne Hoş Geldiniz</>
          }
        </h2>

        <p className="text-white/65 text-sm md:text-base leading-relaxed max-w-lg">
          {isEn
            ? 'We warmly welcome international students from around the world. Join one of Turkey\'s most prestigious science faculties and build your future alongside leading researchers in Actuarial Science, Biology, Chemistry, Mathematics and Statistics.'
            : 'Dünyanın dört bir yanından gelen uluslararası öğrencileri aramızda görmekten büyük mutluluk duyuyoruz. Türkiye\'nin en köklü fen fakültelerinden birinde; Aktüerya, Biyoloji, Kimya, Matematik ve İstatistik alanlarında alanında uzman akademisyenlerle bilimin kapılarını aralayın.'
          }
        </p>
      </div>

      {/* CTA butonlar */}
      <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
        <Link
          to={isEn ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler'}
          className="inline-flex items-center gap-2 bg-white text-gray-900 font-bold text-sm px-6 py-3 rounded-full shadow-lg hover:bg-blue-50 transition-colors duration-200"
        >
          <Globe className="w-4 h-4" />
          {isEn ? 'Learn More' : 'Detaylı Bilgi'}
        </Link>
        <a
          href="https://bilsis.hacettepe.edu.tr/oibs/foa_app/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm px-6 py-3 rounded-full shadow-lg transition-colors duration-200"
        >
          <MousePointer className="w-4 h-4" />
          {isEn ? 'Apply Now' : 'Başvur'}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </motion.div>
);


/* ── Kart Bileşeni ─────────────────────────────────────────────────────────── */
const Card = ({ title, tag, desc, image, href, external, subLinks = [], accent = '#fff', exploreLabel = 'Keşfet' }) => {
  const isExternal = external || (href && href.startsWith('http'));

  const inner = (
    <div className="relative group h-full overflow-hidden rounded-3xl cursor-pointer">
      <img
        src={image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out will-change-transform group-hover:scale-[1.06]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" style={{ height: '55%', top: 'auto' }} />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-7">
        <span
          className="text-[9px] font-black uppercase tracking-[0.25em] mb-3 block opacity-70"
          style={{ color: accent, textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}
        >
          {tag}
        </span>

        <h3
          className="text-xl md:text-2xl font-black text-white leading-tight transition-transform duration-400 group-hover:-translate-y-2"
          style={{ textShadow: '0 2px 12px rgba(0,0,0,0.95)' }}
        >
          {title}
        </h3>

        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 overflow-hidden">
          <div className="overflow-hidden">
            {desc && (
              <p className="text-sm text-white/65 mt-3 leading-relaxed line-clamp-2">{desc}</p>
            )}

            {subLinks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-4">
                {subLinks.slice(0, 5).map((l, i) => (
                  l.href.startsWith('http') ? (
                    <a key={i} href={l.href} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </a>
                  ) : (
                    <Link key={i} to={l.href} onClick={e => e.stopPropagation()}
                       className="text-[11px] font-bold px-3 py-1 rounded-full transition-all duration-200 hover:brightness-125"
                       style={{ background: '#ffffff', color: '#111111', border: 'none', fontWeight: 700 }}>
                      {l.text}
                    </Link>
                  )
                ))}
              </div>
            )}

            <div className="flex items-center gap-1.5 mt-4 text-white text-xs font-bold tracking-wide">
              <span>{exploreLabel}</span>
              <ArrowUpRight size={13} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!href) return inner;
  if (isExternal)
    return <a href={href} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>;
  return <Link to={href} className="block h-full">{inner}</Link>;
};

/* ── Ana Bileşen ───────────────────────────────────────────────────────────── */
const ShowcaseGrid = () => {
  const { t, i18n } = useTranslation();
  const prefix = i18n.language === 'en' ? '/en' : '';

  const isEn = i18n.language === 'en';

  const cards = [
    {
      tag: isEn ? 'Academic' : 'Akademik',
      title: t('showcase.departments'),
      desc: isEn
        ? 'Five world-class departments: Actuarial Science, Biology, Chemistry, Mathematics and Statistics.'
        : 'Aktüerya, Biyoloji, Kimya, Matematik ve İstatistik bölümleriyle bilimin öncüsü.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/MATEMAT%C4%B0K.jpg',
      subLinks: [
        { text: isEn ? 'Actuarial Sci.' : 'Aktüerya',   href: 'https://aktuerya.hacettepe.edu.tr/' },
        { text: isEn ? 'Biology'        : 'Biyoloji',   href: 'https://biology.hacettepe.edu.tr/' },
        { text: isEn ? 'Chemistry'      : 'Kimya',      href: 'https://chem.hacettepe.edu.tr/' },
        { text: isEn ? 'Mathematics'    : 'Matematik',  href: 'https://mat.hacettepe.edu.tr/' },
        { text: isEn ? 'Statistics'     : 'İstatistik', href: 'https://stat.hacettepe.edu.tr/' },
      ],
    },
    {
      tag: isEn ? 'Education' : 'Eğitim',
      title: t('showcase.academicPrograms'),
      desc: isEn
        ? 'Undergraduate, graduate and doctoral programmes for an academic career.'
        : 'Lisans, yüksek lisans ve doktora programlarıyla akademik kariyer.',
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf.jpg',
      href: prefix + '/programlar/lisans',
      subLinks: [
        { text: isEn ? 'Undergraduate' : 'Lisans',        href: prefix + '/programlar/lisans' },
        { text: isEn ? 'Master\'s'     : 'Yüksek Lisans', href: prefix + '/programlar/yuksek-lisans' },
        { text: isEn ? 'Doctorate'     : 'Doktora',       href: prefix + '/programlar/doktora' },
        { text: 'Erasmus',                                 href: prefix + '/programlar/erasmus' },
      ],
    },
    {
      tag: isEn ? 'Research' : 'Araştırma',
      title: t('showcase.researchLabs'),
      desc: t('showcase.labsDesc'),
      image: 'https://lab.hacettepe.edu.tr/fs_/labs/hucrefizyolojisi1.jpg',
      href: prefix + '/arastirma/laboratuvarlar',
    },
    {
      tag: isEn ? 'Faculty' : 'Kadro',
      title: t('showcase.faculty'),
      desc: t('showcase.facultyDesc'),
      image: 'https://fs.hacettepe.edu.tr/fenyeni/books/hf1.jpg',
      href: prefix + '/fakulte-personeli/ogretim-uyeleri',
    },
    {
      tag: isEn ? 'Publication' : 'Yayın',
      title: t('showcase.journals'),
      desc: t('showcase.journalsDesc'),
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80',
      href: prefix + '/arastirma/dergiler',
    },
    {
      tag: isEn ? 'Research & Innovation' : 'Araştırma & İnovasyon',
      title: t('showcase.ongoingProjects'),
      desc: t('showcase.projectsDesc'),
      image: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&w=1600&q=80',
      href: prefix + '/arastirma/devam-eden-projelerimiz',
    },
  ];

  return (
    <section className="bg-white py-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Uluslararası Öğrenciler Banner ── */}
        <InternationalBanner isEn={isEn} prefix={prefix} />

        {/* ── Başlık ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#7B0D1E] mb-2.5">
              {isEn ? 'Hacettepe University · Faculty of Science' : 'Hacettepe Üniversitesi · Fen Fakültesi'}
            </p>
            <h2 className="text-3xl md:text-[2.25rem] font-black text-gray-900 leading-tight">
              {t('showcase.title')}
            </h2>
          </div>
          <p className="text-gray-400 text-sm max-w-xs leading-relaxed md:text-right md:pb-1">
            {t('showcase.subtitle')}
          </p>
        </motion.div>

        {/* ── 3×2 Eşit Grid ── */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        >
          {cards.map((card, i) => (
            <div key={i} style={{ height: '280px' }}>
              <Card {...card} exploreLabel={isEn ? 'Explore' : 'Keşfet'} />
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ShowcaseGrid;
